import React from 'react';
import './CalculationModal.css';
import { IconInfo } from './Icons';

function CalculationModal({ isOpen, onClose, metricType, metrics, formData }) {
  if (!isOpen) return null;

  const getCalculationDetails = () => {
    if (!metrics) return null;
    
    // Use default values if formData is not available
    if (!formData) {
      formData = {
        accountSize: metrics.accountSize || 50000,
        contracts: metrics.numContracts || 1,
        contractType: metrics.contractType || 'NQ',
        maxDrawdown: metrics.drawdownBreach?.maxDrawdown || null,
        startOfDayProfit: null,
        safetyNet: null,
        currentBalance: null
      };
    }

    const accountSize = formData.accountSize || 0;
    const contracts = formData.contracts || 1;
    const contractType = formData.contractType || 'NQ';
    const contractMultiplier = contractType === 'MNQ' ? 0.1 : 1.0;
    const activeConsistencyPercent = Number(metrics.consistencyPercent || metrics.windfallRule?.consistencyPercent || 50);
    const consistencyRatio = activeConsistencyPercent / 100;

    switch (metricType) {
      case 'riskScore': {
        const riskBasisLoss = metrics.riskBasisLoss ?? metrics.highestLoss ?? 0;
        const riskBasisPercent = metrics.riskBasisLossPercent ?? metrics.highestLossPercent ?? 0;
        const drawdownUsagePercent = formData.maxDrawdown
          ? ((riskBasisLoss / formData.maxDrawdown) * 100).toFixed(2)
          : 0;
        return {
          title: 'Risk Score Calculation',
          formula: 'Risk Score = (Worst Loss % of Account × 50) + (Drawdown Usage % × 50)',
          steps: [
            {
              label: 'Risk-Basis Loss % of Account',
              calculation: `(${riskBasisLoss?.toFixed(2) || 0} / ${accountSize}) × 100`,
              result: `${riskBasisPercent}%`
            },
            {
              label: 'Drawdown Usage %',
              calculation: formData.maxDrawdown 
                ? `(${riskBasisLoss?.toFixed(2) || 0} / ${formData.maxDrawdown}) × 100`
                : 'N/A (No max drawdown set)',
              result: formData.maxDrawdown && metrics.drawdownBreach
                ? `${drawdownUsagePercent}%`
                : 'N/A'
            },
            {
              label: 'Final Risk Score',
              calculation: `(${riskBasisPercent}% × 50) + (${formData.maxDrawdown ? drawdownUsagePercent : 0}% × 50)`,
              result: `${metrics.riskScore || 0}/100`
            },
            {
              label: 'Conservative Risk Rule',
              calculation:
                metrics.riskCalculationMethod === 'max_historical_vs_sl'
                  ? 'Use max(historical loss, SL-based theoretical loss)'
                  : 'Use historical loss only',
              result:
                metrics.riskCalculationMethod === 'max_historical_vs_sl'
                  ? `max($${(metrics.historicalHighestLoss || 0).toFixed(2)}, $${(metrics.theoreticalStopLoss || 0).toFixed(2)}) = $${riskBasisLoss.toFixed(2)}`
                  : `$${riskBasisLoss.toFixed(2)}`
            }
          ],
          inputs: {
            'Account Size': `$${accountSize.toLocaleString()}`,
            'Default Contract Size Multiplier': contracts,
            'Contract Type': contractType,
            'Max Drawdown': formData.maxDrawdown ? `$${parseFloat(formData.maxDrawdown).toLocaleString()}` : 'Not set'
          }
        };
      }

      case 'highestLoss':
        return {
          title: 'Highest Loss Calculation',
          formula: 'Highest Loss = Max Historical Loss Per Contract × Default Contract Size Multiplier',
          steps: [
            {
              label: 'Max Historical Loss Per Contract',
              calculation: `From Google Sheet data (${contractType === 'MNQ' ? 'divided by 10 for MNQ' : 'NQ value'})`,
              result: `$${metrics.highestLossPerContract?.toFixed(2) || 0}`
            },
            {
              label: 'Contract Multiplier',
              calculation: contractType === 'MNQ' ? 'MNQ = 0.1 × NQ value' : 'NQ = 1.0 × sheet value',
              result: contractMultiplier
            },
            {
              label: 'Default Contract Size Multiplier',
              calculation: 'User input',
              result: contracts
            },
            {
              label: 'Final Highest Loss',
              calculation: `${metrics.highestLossPerContract?.toFixed(2) || 0} × ${contracts}`,
              result: `$${metrics.highestLoss?.toFixed(2) || 0}`
            },
            {
              label: 'As % of Account',
              calculation: `(${metrics.highestLoss?.toFixed(2) || 0} / ${accountSize}) × 100`,
              result: `${metrics.highestLossPercent || 0}%`
            },
            ...(metrics.theoreticalStopLoss !== null && metrics.theoreticalStopLoss !== undefined
              ? [{
                  label: 'SL-Based Theoretical Loss (Reference)',
                  calculation: `${metrics.strategySlLimitPoints || 0} pts × ${contractType === 'MNQ' ? '$2/pt' : '$20/pt'} × ${metrics.strategySlRatio || 1} × ${contracts}`,
                  result: `$${metrics.theoreticalStopLoss?.toFixed(2) || 0}`
                }]
              : [])
          ],
          inputs: {
            'Account Size': `$${accountSize.toLocaleString()}`,
            'Default Contract Size Multiplier': contracts,
            'Contract Type': contractType,
            'Trading Days Analyzed': metrics.totalDays || 0
          }
        };

      case 'slLoss':
        return {
          title: 'SL-Based Theoretical Loss',
          formula: 'Theoretical SL Loss = SL Limit Points × $/Point × SL Ratio × Default Contract Size Multiplier',
          steps: [
            {
              label: 'SL Limit Points',
              calculation: 'From strategy metadata file',
              result: metrics.strategySlLimitPoints || 0
            },
            {
              label: 'Point Value',
              calculation: contractType === 'MNQ' ? 'MNQ = $2 per point' : 'NQ = $20 per point',
              result: contractType === 'MNQ' ? '$2.00' : '$20.00'
            },
            {
              label: 'SL Ratio',
              calculation: 'From strategy metadata file',
              result: metrics.strategySlRatio || 1
            },
            {
              label: 'Per-Contract Theoretical SL',
              calculation: `${metrics.strategySlLimitPoints || 0} × ${contractType === 'MNQ' ? 2 : 20} × ${metrics.strategySlRatio || 1}`,
              result: `$${metrics.theoreticalStopLossPerContract?.toFixed(2) || 0}`
            },
            {
              label: 'Final Theoretical SL (Position Size)',
              calculation: `${metrics.theoreticalStopLossPerContract?.toFixed(2) || 0} × ${contracts}`,
              result: `$${metrics.theoreticalStopLoss?.toFixed(2) || 0}`
            },
            {
              label: 'As % of Account',
              calculation: `(${metrics.theoreticalStopLoss?.toFixed(2) || 0} / ${accountSize}) × 100`,
              result: `${metrics.theoreticalStopLossPercent || 0}%`
            }
          ],
          inputs: {
            'Account Size': `$${accountSize.toLocaleString()}`,
            'Default Contract Size Multiplier': contracts,
            'Contract Type': contractType,
            'Trading Days Analyzed': metrics.totalDays || 0
          }
        };

      case 'averageLoss':
        return {
          title: 'Average Loss Calculation',
          formula: 'Average Loss = Sum of All Losses / Number of Losing Days × Default Contract Size Multiplier',
          steps: [
            {
              label: 'Sum of All Losses Per Contract',
              calculation: 'Sum of all negative P&L days from historical data',
              result: `$${(metrics.avgLossPerContract * metrics.losingDays)?.toFixed(2) || 0}`
            },
            {
              label: 'Number of Losing Days',
              calculation: 'Count of days with negative P&L',
              result: metrics.losingDays || 0
            },
            {
              label: 'Average Loss Per Contract',
              calculation: `Sum of losses / ${metrics.losingDays || 1} losing days`,
              result: `$${metrics.avgLossPerContract?.toFixed(2) || 0}`
            },
            {
              label: 'Default Contract Size Multiplier',
              calculation: 'User input',
              result: contracts
            },
            {
              label: 'Final Average Loss',
              calculation: `${metrics.avgLossPerContract?.toFixed(2) || 0} × ${contracts}`,
              result: `$${metrics.avgLoss?.toFixed(2) || 0}`
            },
            {
              label: 'As % of Account',
              calculation: `(${metrics.avgLoss?.toFixed(2) || 0} / ${accountSize}) × 100`,
              result: `${metrics.avgLossPercent || 0}%`
            }
          ],
          inputs: {
            'Account Size': `$${accountSize.toLocaleString()}`,
            'Default Contract Size Multiplier': contracts,
            'Contract Type': contractType,
            'Losing Days': metrics.losingDays || 0,
            'Total Trading Days': metrics.totalDays || 0
          }
        };

      case 'maxProfit':
        return {
          title: 'Maximum Profit Calculation',
          formula: 'Maximum Profit = Max Historical Profit Per Contract × Default Contract Size Multiplier',
          steps: [
            {
              label: 'Max Historical Profit Per Contract',
              calculation: `From Google Sheet data (${contractType === 'MNQ' ? 'divided by 10 for MNQ' : 'NQ value'})`,
              result: `$${metrics.maxProfitPerContract?.toFixed(2) || 0}`
            },
            {
              label: 'Contract Multiplier',
              calculation: contractType === 'MNQ' ? 'MNQ = 0.1 × NQ value' : 'NQ = 1.0 × sheet value',
              result: contractMultiplier
            },
            {
              label: 'Default Contract Size Multiplier',
              calculation: 'User input',
              result: contracts
            },
            {
              label: 'Final Maximum Profit',
              calculation: `${metrics.maxProfitPerContract?.toFixed(2) || 0} × ${contracts}`,
              result: `$${metrics.maxProfit?.toFixed(2) || 0}`
            },
            {
              label: 'As % of Account',
              calculation: `(${metrics.maxProfit?.toFixed(2) || 0} / ${accountSize}) × 100`,
              result: `${metrics.maxProfitPercent || 0}%`
            }
          ],
          inputs: {
            'Account Size': `$${accountSize.toLocaleString()}`,
            'Default Contract Size Multiplier': contracts,
            'Contract Type': contractType,
            'Winning Days': metrics.winningDays || 0
          }
        };

      case 'averageProfit':
        return {
          title: 'Average Profit Calculation',
          formula: 'Average Profit = Sum of All Profits / Number of Winning Days × Default Contract Size Multiplier',
          steps: [
            {
              label: 'Sum of All Profits Per Contract',
              calculation: 'Sum of all positive P&L days from historical data',
              result: `$${(metrics.avgProfitPerContract * metrics.winningDays)?.toFixed(2) || 0}`
            },
            {
              label: 'Number of Winning Days',
              calculation: 'Count of days with positive P&L',
              result: metrics.winningDays || 0
            },
            {
              label: 'Average Profit Per Contract',
              calculation: `Sum of profits / ${metrics.winningDays || 1} winning days`,
              result: `$${metrics.avgProfitPerContract?.toFixed(2) || 0}`
            },
            {
              label: 'Default Contract Size Multiplier',
              calculation: 'User input',
              result: contracts
            },
            {
              label: 'Final Average Profit',
              calculation: `${metrics.avgProfitPerContract?.toFixed(2) || 0} × ${contracts}`,
              result: `$${metrics.avgProfit?.toFixed(2) || 0}`
            },
            {
              label: 'As % of Account',
              calculation: `(${metrics.avgProfit?.toFixed(2) || 0} / ${accountSize}) × 100`,
              result: `${metrics.avgProfitPercent || 0}%`
            }
          ],
          inputs: {
            'Account Size': `$${accountSize.toLocaleString()}`,
            'Default Contract Size Multiplier': contracts,
            'Contract Type': contractType,
            'Winning Days': metrics.winningDays || 0,
            'Total Trading Days': metrics.totalDays || 0
          }
        };

      case 'maxDrawdown':
        if (!metrics.drawdownBreach) return null;
        return {
          title: 'Max Drawdown Analysis Calculation',
          formula: 'Breach Probability = (Days Exceeding Max Drawdown / Total Trading Days) × 100',
          steps: [
            {
              label: 'Max Drawdown Limit',
              calculation: 'User input',
              result: `$${parseFloat(formData.maxDrawdown).toLocaleString()}`
            },
            {
              label: 'Risk-Basis Loss Used For Limits',
              calculation: `Conservative loss used with ${contracts} contract(s)`,
              result: `$${metrics.riskBasisLoss?.toFixed(2) || metrics.highestLoss?.toFixed(2) || 0}`
            },
            {
              label: 'Days Exceeding Limit',
              calculation: 'Count of days where loss exceeded max drawdown',
              result: `${metrics.drawdownBreach.breaches || 0} day(s)`
            },
            {
              label: 'Total Trading Days',
              calculation: 'Total days analyzed',
              result: metrics.totalDays || 0
            },
            {
              label: 'Breach Probability',
              calculation: `(${metrics.drawdownBreach.breaches || 0} / ${metrics.totalDays || 1}) × 100`,
              result: `${metrics.drawdownBreach.breachProbability || 0}%`
            },
            {
              label: 'Buffer Above Highest Loss',
              calculation: `${parseFloat(formData.maxDrawdown)} - ${(metrics.riskBasisLoss ?? metrics.highestLoss)?.toFixed(2) || 0}`,
              result: `$${parseFloat(metrics.drawdownBreach.margin).toFixed(2)}`
            }
          ],
          inputs: {
            'Account Size': `$${accountSize.toLocaleString()}`,
            'Default Contract Size Multiplier': contracts,
            'Max Drawdown': `$${parseFloat(formData.maxDrawdown).toLocaleString()}`,
            'Total Trading Days': metrics.totalDays || 0
          }
        };

      case 'apexMae':
        if (!metrics.apexMaeComparison) return null;
        const startOfDayProfit = formData.startOfDayProfit || 0;
        const safetyNet = formData.safetyNet || 0;
        const baseAmount = metrics.apexMaeComparison.baseAmount || 0;
        const limitPercent = metrics.apexMaeComparison.limitPercent || consistencyRatio;
        
        return {
          title: `Consistency Limit Calculation (${activeConsistencyPercent}% Rule)`,
          formula: 'Consistency Limit = Base Amount × Limit Percentage',
          steps: [
            {
              label: 'Start-of-Day Profit',
              calculation: 'Current Account Balance - Account Size',
              result: `$${startOfDayProfit.toFixed(2)}`
            },
            {
              label: 'Safety Net (Trailing Threshold)',
              calculation: 'Auto-set based on account size',
              result: `$${safetyNet.toFixed(2)}`
            },
            {
              label: 'Base Amount Determination',
              calculation: startOfDayProfit <= safetyNet && safetyNet > 0
                ? `Profit (${startOfDayProfit.toFixed(2)}) ≤ Safety Net (${safetyNet.toFixed(2)}) → Use Safety Net`
                : `Profit (${startOfDayProfit.toFixed(2)}) > Safety Net (${safetyNet.toFixed(2)}) → Use Profit`,
              result: `$${baseAmount.toFixed(2)}`
            },
            {
              label: 'Limit Percentage',
              calculation: `Selected consistency rule percentage`,
              result: `${(limitPercent * 100).toFixed(0)}%`
            },
            {
              label: 'Consistency Limit',
              calculation: `${baseAmount.toFixed(2)} × ${limitPercent.toFixed(2)}`,
              result: `$${metrics.apexMaeComparison.maxMaePerTrade?.toFixed(2) || 0}`
            },
            {
              label: 'Risk-Basis Loss Used For Consistency Check',
              calculation: `Conservative loss with ${contracts} contract(s)`,
              result: `$${metrics.riskBasisLoss?.toFixed(2) || metrics.highestLoss?.toFixed(2) || 0}`,
              note: metrics.riskCalculationMethod === 'max_historical_vs_sl'
                ? 'Uses max of historical and SL-based loss'
                : 'Based on historical strategy data'
            },
            {
              label: 'Status',
              calculation: metrics.apexMaeComparison.exceedsMae
                ? `Risk-Basis Loss (${(metrics.riskBasisLoss ?? metrics.highestLoss)?.toFixed(2)}) > Consistency Limit (${metrics.apexMaeComparison.maxMaePerTrade?.toFixed(2)})`
                : `Risk-Basis Loss (${(metrics.riskBasisLoss ?? metrics.highestLoss)?.toFixed(2)}) ≤ Consistency Limit (${metrics.apexMaeComparison.maxMaePerTrade?.toFixed(2)})`,
              result: metrics.apexMaeComparison.exceedsMae ? 'NO GO' : 'GO'
            }
          ],
          inputs: {
            'Account Size': `$${accountSize.toLocaleString()}`,
            'Current Account Balance': formData.currentBalance ? `$${parseFloat(formData.currentBalance).toLocaleString()}` : 'N/A',
            'Start-of-Day Profit': `$${startOfDayProfit.toFixed(2)}`,
            'Safety Net': `$${safetyNet.toFixed(2)}`,
            'Default Contract Size Multiplier': contracts,
            'Contract Type': contractType
          }
        };

      case 'windfall':
        if (!metrics.windfallRule) {
          return null;
        }
        
        const apexMinPayoutProfit = metrics.windfallRule.apexMinPayoutProfit 
          ? parseFloat(metrics.windfallRule.apexMinPayoutProfit) 
          : 2600;
        const largestSafeDayForFastestPayout = metrics.windfallRule.largestSafeDayForFastestPayout 
          ? parseFloat(metrics.windfallRule.largestSafeDayForFastestPayout) 
          : 780;
        const highestProfitDay = metrics.windfallRule.maxProfitDay || metrics.maxProfit || 0;
        const totalProfitRequiredForPayout = metrics.windfallRule.totalProfitRequiredForPayout 
          ? parseFloat(metrics.windfallRule.totalProfitRequiredForPayout) 
          : null;
        const additionalProfitNeededBeyondMinimum = metrics.windfallRule.additionalProfitNeededBeyondMinimum 
          ? parseFloat(metrics.windfallRule.additionalProfitNeededBeyondMinimum) 
          : 0;
        
        if (!highestProfitDay || highestProfitDay <= 0) {
          return null;
        }
        
        return {
          title: `Fastest Payout Path Calculation (${activeConsistencyPercent}% Consistency Rule)`,
          formula: `Largest Safe Strategy Profit Day = Apex Minimum Payout Profit × ${(consistencyRatio).toFixed(2)}`,
          explanation: `Based on Apex $50k account minimum payout profit of $2,600. If the strategy's largest profit day stays under $${largestSafeDayForFastestPayout.toFixed(2)} (${activeConsistencyPercent}% of $2,600), a $2,600 payout is achievable.`,
          steps: [
            {
              label: 'Apex 50K Account Minimum Payout Profit',
              calculation: 'Fixed constant for Apex Trader Funding',
              result: `$${apexMinPayoutProfit.toFixed(2)}`
            },
            {
              label: 'Largest Safe Strategy Profit Day for Fastest Payout',
              calculation: `${apexMinPayoutProfit.toFixed(2)} × ${(consistencyRatio).toFixed(2)}`,
              result: `$${largestSafeDayForFastestPayout.toFixed(2)}`,
              note: 'If the strategy\'s largest profit day stays under this level, a $2,600 payout is achievable.'
            },
            {
              label: 'Strategy\'s Highest Historical Profit Day',
              calculation: 'From historical strategy data',
              result: `$${highestProfitDay.toFixed(2)}`,
              note: 'Based on historical strategy performance, not your personal trading results.'
            },
            {
              label: 'Total Profit Required for Payout',
              calculation: `Highest Profit Day ÷ ${(consistencyRatio).toFixed(2)} = ${highestProfitDay.toFixed(2)} ÷ ${(consistencyRatio).toFixed(2)}`,
              result: totalProfitRequiredForPayout ? `$${totalProfitRequiredForPayout.toFixed(2)}` : 'N/A',
              note: `To request payout, you need at least this much total profit so your highest day is ≤ ${activeConsistencyPercent}% of total.`
            },
            {
              label: 'Additional Profit Needed Beyond Minimum',
              calculation: totalProfitRequiredForPayout 
                ? `Total Required (${totalProfitRequiredForPayout.toFixed(2)}) - Minimum (${apexMinPayoutProfit.toFixed(2)})`
                : 'N/A',
              result: additionalProfitNeededBeyondMinimum > 0 
                ? `+$${additionalProfitNeededBeyondMinimum.toFixed(2)}`
                : '$0.00',
              note: additionalProfitNeededBeyondMinimum > 0 
                ? 'Additional profit needed beyond the minimum payout requirement.'
                : 'Strategy is aligned with fastest payout path.'
            },
            {
              label: 'Status',
              calculation: highestProfitDay <= largestSafeDayForFastestPayout
                ? `Highest Day (${highestProfitDay.toFixed(2)}) ≤ Safe Threshold (${largestSafeDayForFastestPayout.toFixed(2)})`
                : `Highest Day (${highestProfitDay.toFixed(2)}) > Safe Threshold (${largestSafeDayForFastestPayout.toFixed(2)})`,
              result: highestProfitDay <= largestSafeDayForFastestPayout ? 'ALIGNED' : 'DELAYED'
            }
          ],
          inputs: {
            'Apex Minimum Payout Profit': `$${apexMinPayoutProfit.toFixed(2)}`,
            'Strategy\'s Highest Historical Profit Day': `$${highestProfitDay.toFixed(2)}`,
            'Total Profit Required for Payout': totalProfitRequiredForPayout ? `$${totalProfitRequiredForPayout.toFixed(2)}` : 'N/A',
            'Additional Profit Needed Beyond Minimum': additionalProfitNeededBeyondMinimum > 0 ? `+$${additionalProfitNeededBeyondMinimum.toFixed(2)}` : '$0.00',
            'Default Contract Size Multiplier': contracts,
            'Contract Type': contractType,
            'Total Trading Days': metrics.windfallRule.totalTradingDays || 0
          }
        };

      default:
        return null;
    }
  };

  const details = getCalculationDetails();
  if (!details) {
    return (
      <div className="calculation-modal-overlay" onClick={onClose}>
        <div className="calculation-modal" onClick={(e) => e.stopPropagation()}>
          <div className="calculation-modal-header">
            <div className="calculation-modal-title">
              <IconInfo size={24} />
              <h2>Calculation Details</h2>
            </div>
            <button className="calculation-modal-close" onClick={onClose}>×</button>
          </div>
          <div className="calculation-modal-content">
            <p style={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center', padding: '2rem' }}>
              Calculation details are not available. Please analyze your strategy first to see how metrics are calculated.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="calculation-modal-overlay" onClick={onClose}>
      <div className="calculation-modal" onClick={(e) => e.stopPropagation()}>
        <div className="calculation-modal-header">
          <div className="calculation-modal-title">
            <IconInfo size={24} />
            <h2>{details.title}</h2>
          </div>
          <button className="calculation-modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="calculation-modal-content">
          <div className="formula-section">
            <h3>Formula</h3>
            <div className="formula-box">
              {details.formula}
            </div>
          </div>

          <div className="steps-section">
            <h3>Step-by-Step Calculation</h3>
            {details.steps.map((step, index) => (
              <div key={index} className="calculation-step">
                <div className="step-number">{index + 1}</div>
                <div className="step-content">
                  <div className="step-label">{step.label}</div>
                  <div className="step-calculation">{step.calculation}</div>
                  <div className="step-result">= {step.result}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="inputs-section">
            <h3>Input Values</h3>
            <div className="inputs-grid">
              {Object.entries(details.inputs).map(([key, value]) => (
                <div key={key} className="input-item">
                  <span className="input-label">{key}:</span>
                  <span className="input-value">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CalculationModal;

